import { useSearchParams } from 'react-router-dom'

import { fetchFips, fetchOrganizations } from '@/api'
import { FipCard } from '@/components/cards'
import { ErrorState, Loading, PageHead, PageShell, Pagination, Term } from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import type { Fip, FipStatus } from '@/types'

const PAGE_SIZE = 12
const fipStatuses: FipStatus[] = ['published', 'draft']
const sorts = ['updated', 'title', 'coverage'] as const

function principleCount(fip: Fip): number {
    return new Set(fip.declarations.map((item) => item.principle)).size
}

export function Profiles() {
    const { t, localized, language } = useI18n()
    const [params, setParams] = useSearchParams()
    const fips = useAsync(fetchFips, [])
    const organizations = useAsync(fetchOrganizations, [])

    if (fips.error) {
        return (
            <PageShell>
                <ErrorState error={fips.error} onRetry={fips.reload} />
            </PageShell>
        )
    }
    if (!fips.data) {
        return (
            <PageShell>
                <Loading />
            </PageShell>
        )
    }

    const query = params.get('q') ?? ''
    const domain = params.get('domain') ?? ''
    const status = params.get('status') ?? ''
    const verified = params.get('verified') ?? ''
    const sort = params.get('sort') ?? 'updated'

    /** Changing a filter always returns to the first page. */
    const update = (key: string, value: string) => {
        const next = new URLSearchParams(params)
        next.delete('page')
        if (value) {
            next.set(key, value)
        } else {
            next.delete(key)
        }
        setParams(next, { replace: true })
    }

    const communities = new Map((organizations.data ?? []).map((item) => [item.id, item]))
    const domains = [...new Map(fips.data.map((fip) => [fip.domain.en, fip.domain])).values()].sort(
        (a, b) => localized(a).localeCompare(localized(b), language),
    )

    const needle = query.trim().toLowerCase()
    const matches = fips.data
        .filter((fip) => {
            const community = communities.get(fip.community)
            const haystack = [
                fip.title,
                fip.summary,
                fip.domain,
                community?.name,
                { en: fip.acronym ?? '', cs: community?.acronym ?? '' },
            ]
                .flatMap((text) => (text ? [text.en, text.cs] : []))
                .join(' ')
                .toLowerCase()
            return (
                haystack.includes(needle) &&
                (!domain || fip.domain.en === domain) &&
                (!status || fip.status === status) &&
                (!verified ||
                    fip.verification.some((item) => item.level === 'verified') ===
                        (verified === 'true'))
            )
        })
        .sort((a, b) => {
            if (sort === 'title') {
                return localized(a.title).localeCompare(localized(b.title), language)
            }
            if (sort === 'coverage') {
                return principleCount(b) - principleCount(a)
            }
            return b.updated.localeCompare(a.updated)
        })

    const pageCount = Math.max(1, Math.ceil(matches.length / PAGE_SIZE))
    const page = Math.min(Math.max(1, Number(params.get('page')) || 1), pageCount)
    const shown = matches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    const hasFilters = Boolean(query || domain || status || verified)

    const goTo = (target: number) => {
        const next = new URLSearchParams(params)
        next.set('page', String(target))
        setParams(next)
        window.scrollTo(0, 0)
    }

    return (
        <PageShell>
            <PageHead title={t('fips.title')} lead={t('fips.lead')} term="fip" />

            <div className="filters card">
                <div className="filters__row">
                    <div className="field field--grow">
                        <label htmlFor="profile-search">{t('common.search')}</label>
                        <input
                            id="profile-search"
                            type="search"
                            value={query}
                            placeholder={t('fips.searchPlaceholder')}
                            onChange={(event) => update('q', event.target.value)}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="profile-domain">{t('fips.filter.domain')}</label>
                        <select
                            id="profile-domain"
                            value={domain}
                            onChange={(event) => update('domain', event.target.value)}
                        >
                            <option value="">—</option>
                            {domains.map((item) => (
                                <option key={item.en} value={item.en}>
                                    {localized(item)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="field">
                        <label htmlFor="profile-sort">{t('fips.sort')}</label>
                        <select
                            id="profile-sort"
                            value={sort}
                            onChange={(event) => update('sort', event.target.value)}
                        >
                            {sorts.map((item) => (
                                <option key={item} value={item}>
                                    {t(`fips.sort.${item}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="filters__row">
                    <div className="field">
                        <label>{t('fips.filter.status')}</label>
                        <div className="toggle-group">
                            {fipStatuses.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    className="toggle"
                                    aria-pressed={status === item}
                                    onClick={() => update('status', status === item ? '' : item)}
                                >
                                    {t(`fip.status.${item}`)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="field">
                        <label>
                            {t('verification.filter')} <Term id="verification" />
                        </label>
                        <div className="toggle-group">
                            {[
                                ['true', t('verification.verifiedOnly')],
                                ['false', t('verification.unverifiedOnly')],
                            ].map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    className="toggle"
                                    aria-pressed={verified === value}
                                    onClick={() =>
                                        update('verified', verified === value ? '' : value)
                                    }
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {hasFilters ? (
                    <div>
                        <button
                            className="button"
                            type="button"
                            onClick={() => setParams(new URLSearchParams(), { replace: true })}
                        >
                            {t('common.clear')}
                        </button>
                    </div>
                ) : null}
            </div>

            <p className="results-count">
                {t('fips.count')
                    .replace('{shown}', String(matches.length))
                    .replace('{total}', String(fips.data.length))}
            </p>

            {shown.length ? (
                <div className="grid grid--cards">
                    {shown.map((fip) => (
                        <FipCard
                            key={fip.id}
                            fip={fip}
                            community={communities.get(fip.community)}
                            verifiers={communities}
                        />
                    ))}
                </div>
            ) : (
                <p className="empty">{t('common.noResults')}</p>
            )}

            <Pagination page={page} count={pageCount} onChange={goTo} />
        </PageShell>
    )
}
